import { useEffect, lazy, Suspense } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom'
import ScrollToTop from './components/scrollToTop/ScrollToTop'
import { MainContext } from './MainContext'
import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import i18n from './i18n'
import moment from 'moment-timezone'
import 'moment/locale/fo'
import 'moment/locale/da'
import 'moment/locale/de'
import LoadingIndicator from './components/loadingIndicator'
import {
  GetUserInput,
  GetUserResponse,
  GET_USER,
} from './operations/queries/getUser'
import Header from './components/header/Header'
import { HeaderContainer } from './styles/global'
import { setAuthenticatedUser } from './utils/applicationinsights'

const NotificationOverview = lazy(
  () => import('./components/notificationOverview')
)
const Settings = lazy(() => import('./components/settings/Settings'))
const Login = lazy(() => import('./components/login'))

const App = () => {
  const { isAuthenticated, user, isLoading } = useAuth0()

  if (isLoading) return <LoadingIndicator fullscreen />

  if (!isAuthenticated || !user) {
    return (
      <Suspense fallback={null}>
        <Login />
      </Suspense>
    )
  }
  setAuthenticatedUser(
    user['https://hasura.io/jwt/claims']?.['x-hasura-ta-user-id'] ||
      'no ta-user-id in auth0 authorisation'
  )

  return <HasUser user={user}></HasUser>
}

const HasUser = ({ user }: any) => {
  const { t } = useTranslation()

  const userQuery = useQuery<GetUserResponse, GetUserInput>(GET_USER, {
    variables: { email: user.email },
  })

  const currentUser = userQuery.data?.ta_user[0]

  useEffect(() => {
    const fixupDanish = (userLanguage: string) =>
      userLanguage === 'dk' ? 'da' : userLanguage

    const userLanguage = fixupDanish(
      currentUser?.user_language || i18n.language || 'en'
    )
    i18n.changeLanguage(userLanguage)
  }, [currentUser])

  useEffect(() => {
    moment.locale(t(`moment`))
  }, [t])

  if (userQuery.loading) return <LoadingIndicator fullscreen />

  if (!currentUser) {
    return null
  }

  return <Content currentUser={currentUser}></Content>
}

interface ContentProps {
  currentUser: Api.User
}

const Content = ({ currentUser }: ContentProps) => {
  return (
    <MainContext.Provider
      value={{
        currentUser,
      }}
    >
      <Router>
        <HeaderContainer>
          <Header />
        </HeaderContainer>

        <Suspense fallback={null}>
          <Routes>
            <Route path="/callback" element={<NotificationOverview />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/" element={<NotificationOverview />} />
          </Routes>
        </Suspense>
        <ScrollToTop />
      </Router>
    </MainContext.Provider>
  )
}

export default App
